/**
 * **The way out of a queue row's summary and into the whole session** — every exercise, every set,
 * the client's remarks under each of them.
 *
 * Drawn on the rows and cards that name a session: "Trening czeka na feedback" and "Trening w
 * połowie", on the Pulpit, `/zadania` and the client's Przegląd. `null` where the row names none;
 * `model/sessionLink` is the one place that decides, and this component asks it.
 *
 * ── It opens the panel IN PLACE (owner, 25 September 2026) ──────────────────────────────────
 * It used to be a link to the client's card with `?exec=`; the trainer left the screen they were
 * working through. It is a BUTTON now, and the surface lays the one session panel over itself
 * (`onOpen`). It stamps `data-session-opener` (`sessionOpenerKey`, the READ intent), which is where
 * every host puts focus back when the panel closes.
 *
 * ── It does not take the primary's role ──────────────────────────────────────────────────────
 * The row's primary is the act that RESOLVES the item (attention-queue R15). This is the READ, and
 * it is drawn as text at the row's own `--text-2` rather than as a fourth control.
 *
 * The word is mobile's own CTA for exactly this act (`chat.rich.viewSession`), so no new string is
 * minted for a destination both apps already name. The accessible name adds the person — five
 * buttons reading "Zobacz sesję" in a column say nothing about whose.
 */

import { useTranslation } from 'react-i18next';

import { sessionOpenerKey } from '~/shared/lib/sessionOpener';

import { taskLabel } from '../lib/taskLabel';
import type { AttentionItem } from '../model/attentionItems';
import { sessionDestination, type SessionDestination } from '../model/sessionLink';
import styles from './SessionLink.module.css';
import { ActGlyphMark } from './TaskActs';

interface SessionLinkProps {
  readonly item: AttentionItem;
  /** Where the link stands in this row, if the row's own box needs anything said about it. */
  readonly className?: string;
  /**
   * Draw the session's glyph beside the word — in a card's foot, beside a primary that wears its
   * own (owner, 25 September 2026). The Pulpit's task row stacks this link over its controls and
   * keeps the word alone: a 16px mark would outgrow the line and the row with it.
   */
  readonly glyph?: boolean;
  /** Lay the session panel over the surface the trainer is on. */
  readonly onOpen: (destination: SessionDestination) => void;
}

export function SessionLink({ item, className, glyph = false, onOpen }: SessionLinkProps) {
  const { t } = useTranslation();
  const destination = sessionDestination(item);
  if (destination === null) return null;

  const label = t('chat.rich.viewSession');
  return (
    <button
      type="button"
      className={`${styles.link} ${className ?? ''}`}
      data-session-opener={sessionOpenerKey(destination.workoutExecutionId, 'read')}
      aria-label={t('web:home.action.forTask', { action: label, task: taskLabel(item, t) })}
      onClick={() => {
        onOpen(destination);
      }}
    >
      {glyph ? <ActGlyphMark glyph="session" /> : null}
      {label}
    </button>
  );
}
